import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useAuth } from '@/lib/auth';
import { prospectsAPI, usersAPI, ledgerAPI, tasksAPI, teamsV2API, calendarAPI } from '@/lib/api';
import { getCached, swrFetch } from '@/lib/dataCache';
import { 
    Filter, FileText, Eye, Zap, Target, Calendar, Briefcase, 
    ChevronRight, X, Calculator, PieChart, Search, ArrowUpDown, ArrowUp, ArrowDown, User, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

// Dashboard components
import {
    DashboardStatsCards,
    DashboardTasksWidget,
    DashboardEventsWidget,
    DashboardQuickActions,
    VPRankings,
    ApporteurRankings,
    AgendaPlaceholder,
    BankAccountPlaceholder,
    ApporteurDashboardView,
    VPPayeurDashboardView,
    VPSuperviseurDashboardView,
    AdminDashboardView,
    CommissionCells,
    makeModuleCommissions,
    NewsFeedWidget,
    OverdueProspectsWidget,
    UnsecuredProspectsWidget,
    DashboardFilters,
    AcademyUpdateBanner,
    FeatureAnnouncement,
} from '@/components/dashboard';
import { DashboardRestantsStats } from '@/components/dashboard/DashboardRestantsStats';
import { CommissionsPendingWidget } from '@/components/dashboard/CommissionsPendingWidget';
import QuickAccessCards from '@/components/dashboard/QuickAccessCards';
import PilotageDashboard from '@/components/dashboard/PilotageDashboard';
import ParrainageIntroBanner from '@/components/dashboard/ParrainageIntroBanner';
import { PrimeSecteurWidget } from '@/components/dashboard/PrimeSecteurWidget';
import { PrimeSecteurAdminOverview } from '@/components/dashboard/PrimeSecteurAdminOverview';
import WallUnreadWidget from '@/components/dashboard/WallUnreadWidget';
import DirectivesWidget from '@/components/dashboard/DirectivesWidget';
import MyDirectivesStatusWidget from '@/components/dashboard/MyDirectivesStatusWidget';
import AuditedDossiersWidget from '@/components/dashboard/AuditedDossiersWidget';
import DevFundCard from '@/components/dashboard/DevFundCard';
// 🧠 IA Centrale + Bannière profil + Bulle chat (nouvelle refonte Journal)
import UatuGreetingBubble from '@/components/dashboard/UatuGreetingBubble';
import UatuNudgeManager from '@/components/dashboard/UatuNudgeManager';
import UatuWelcomeBanner from '@/components/dashboard/UatuWelcomeBanner';
import UatuDailyPushWatcher from '@/components/dashboard/UatuDailyPushWatcher';
import PersonalizedGreetingBanner from '@/components/dashboard/PersonalizedGreetingBanner';
import AIChatBubble from '@/components/dashboard/AIChatBubble';
import UserProfileModal from '@/components/shared/UserProfileModal';
import { HashtagFilter } from '@/components/prospect/HashtagFilter';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Constants
const PERIOD_OPTIONS = [
    { value: 'day', label: 'Aujourd\'hui' },
    { value: 'week', label: 'Cette semaine' },
    { value: 'month', label: 'Ce mois' },
    { value: 'quarter', label: 'Ce trimestre' },
    { value: 'year', label: 'Cette année' },
    { value: 'all', label: 'Tout' }
];

const EASY_POWER_STAGES = [
    'ep_debut_prospection', 'ep_facture_recue', 'ep_mandat_envoye', 'ep_mandat_signe',
    'ep_dossier_transmis', 'ep_etude_recue', 'ep_etude_expliquee', 'ep_rib_receptionne',
    'ep_contrat_envoye', 'ep_contrat_explique', 'ep_gagne', 'ep_questionnaire_chantier'
];

const CHANTIER_STAGES = [
    'ch_formulaire_rempli', 'ch_visite_planifiee', 'ch_visite_effectuee', 'ch_dossier_etude',
    'ch_etude_presentee', 'ch_devis_envoye', 'ch_devis_accepte', 'ch_planification',
    'ch_en_cours', 'ch_termine', 'ch_attente_paiement', 'ch_paiement_effectue', 'ch_valide'
];

const PIPELINE_OPTIONS = [
    { value: 'both', label: 'Les deux' },
    { value: 'easy_power', label: 'Courtage' },
    { value: 'chantier', label: 'Travaux d’optimisation' }
];

const IN_PROGRESS_PERIOD_OPTIONS = [
    { value: 'all', label: 'Toutes periodes' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: 'Cette semaine' },
    { value: 'month', label: 'Ce mois' },
    { value: 'quarter', label: 'Ce trimestre' },
    { value: 'year', label: 'Cette annee' }
];

// Helper functions
function getStageLabel(stage) {
    const labels = {
        'ep_debut_prospection': 'Début prospection',
        'ep_facture_recue': 'Facture reçue',
        'ep_mandat_envoye': 'Mandat envoyé',
        'ep_mandat_signe': 'Mandat signé',
        'ep_dossier_transmis': 'Dossier transmis',
        'ep_etude_recue': 'Étude reçue',
        'ep_etude_expliquee': 'Étude expliquée',
        'ep_rib_receptionne': 'RIB réceptionné',
        'ep_contrat_envoye': 'Contrat envoyé',
        'ep_contrat_explique': 'Attente signature',
        'ep_gagne': 'Gagné',
        'ep_questionnaire_chantier': 'Formulaire rempli',
        'ch_formulaire_rempli': 'Formulaire rempli',
        'ch_visite_planifiee': 'Visite planifiée',
        'ch_visite_effectuee': 'Visite effectuée',
        'ch_dossier_etude': 'À l\'étude',
        'ch_etude_presentee': 'Étude présentée',
        'ch_devis_envoye': 'Devis envoyé',
        'ch_devis_accepte': 'Devis accepté',
        'ch_planification': 'Planification',
        'ch_en_cours': 'En cours',
        'ch_termine': 'Terminé',
        'ch_attente_paiement': 'Attente paiement',
        'ch_paiement_effectue': 'Payé',
        'ch_valide': 'Validé'
    };
    return labels[stage] || stage || '-';
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // 🎯 Deep-link Uatu : `?focus=<user_id>` — utilisé par Focus tab pour scroller
    // vers un apporteur précis. Exposé aux enfants via prop implicite (searchParams
    // partagé). Le `tab` initial est aussi lu depuis l'URL (voir plus bas).
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    
    // Data states
    const [prospects, setProspects] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [allUsers, setAllUsers] = useState([]); // Pour les vues admin
    const [allTeams, setAllTeams] = useState([]); // Pour les vues VP superviseur
    const [stats, setStats] = useState({
        totalProspects: 0,
        inProgressCount: 0,
        validatedCount: 0,
        totalCommission: 0,
        avgCommission: 0,
        commissionsValidees: 0,
        commissionsAAllerChercher: 0,
        prospectsGagnes: [],
        prospectsAAllerChercher: []
    });
    
    // Dialog states
    const [showProspectsDialog, setShowProspectsDialog] = useState(false);
    const [dialogProspects, setDialogProspects] = useState([]);
    const [dialogTitle, setDialogTitle] = useState('');
    const [filterStage, setFilterStage] = useState('all');
    
    // Filters for "Dossiers en cours"
    const [pipelineFilter, setPipelineFilter] = useState('both');
    const [selectedEPStages, setSelectedEPStages] = useState([]);
    const [selectedChStages, setSelectedChStages] = useState([]);
    const [inProgressPeriod, setInProgressPeriod] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedHashtags, setSelectedHashtags] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    
    // Commission Dialog
    const [showCommissionDialog, setShowCommissionDialog] = useState(false);
    const [commissionData, setCommissionData] = useState({
        commissions: [],
        totalByPeriod: {},
        totalByPipeline: {},
        totalByStatus: {}
    });
    const [commissionPeriodFilter, setCommissionPeriodFilter] = useState('all');
    
    // Commission Details Dialog (validées / à aller chercher)
    const [showCommissionDetailsDialog, setShowCommissionDetailsDialog] = useState(false);
    const [commissionDetailsType, setCommissionDetailsType] = useState('validees'); // 'validees' ou 'a_chercher'
    
    // Tasks widget state
    const [tasksData, setTasksData] = useState({ tasks: [], stats: {} });
    const [eventsData, setEventsData] = useState({ events: [] });
    
    // User Profile Modal state
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [showUserProfileModal, setShowUserProfileModal] = useState(false);
    const [loadError, setLoadError] = useState(null);
    
    // Advanced Filters state - DÉFAUT SUR "CE MOIS"
    const [advancedFilters, setAdvancedFilters] = useState({
        periodType: 'month',
        dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        dateTo: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999),
        scopeType: 'all',
        teamId: null,
        userId: null
    });

    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';

    useEffect(() => {
        // Admin & VP utilisent la nouvelle vue Pilotage (chargement dédié, léger).
        // On évite le téléchargement complet des prospects pour eux, mais on charge
        // tout de même leurs tâches pour le widget "Mes tâches".
        if (isAdminFull || isAdmin || isVP) {
            loadTasksData();
            loadEventsData();
            // Chargement LÉGER dédié aux VP pour alimenter les vues dissociées
            // (Mon Équipe / Équipes supervisées) sans télécharger 100 000 prospects.
            if (isVP) {
                loadVpDashboardLight();
            }
            setLoading(false);
            return;
        }
        loadDashboard();
        loadCommissionData();
        loadTasksData();
        loadEventsData();
    }, []);
    
    // Recalculer les stats quand les filtres avancés changent
    useEffect(() => {
        if (prospects.length > 0) {
            calculateStats(prospects, advancedFilters);
        }
    }, [advancedFilters, allTeams]);
    
    // Handler pour les changements de filtres avancés
    const handleFiltersChange = (newFilters) => {
        setAdvancedFilters(newFilters);
    };

    // Chargement LÉGER pour les VP : alimente les vues dissociées (Mon Équipe /
    // Équipes supervisées) sans télécharger la totalité des prospects (payload réduit
    // via /dashboard/prospects-light). Idempotent, échoue en silence.
    const loadVpDashboardLight = async () => {
        try {
            const [teamsRes, prospectsRes, usersRes] = await Promise.all([
                teamsV2API.getAll().catch(() => ({ data: [] })),
                prospectsAPI.getDashboardLight({ timeout: 45000 }).catch(() => ({ data: [] })),
                usersAPI.getAll().catch(() => ({ data: [] })),
            ]);
            setAllTeams(teamsRes.data || []);
            setProspects(prospectsRes.data || []);
            setAllUsers(usersRes.data || []);
            try {
                const teamRes = await usersAPI.getVPTeam(user.id);
                setTeamMembers(teamRes.data || []);
            } catch { /* ignore */ }
        } catch (e) {
            console.error('loadVpDashboardLight error', e);
        }
    };

    const loadDashboard = async () => {
        setLoadError(null);
        const baseKey = `dashboard:base:${user?.id}`;
        const cached = getCached(baseKey);
        if (cached) {
            // Affichage instantané du déjà-vu
            setProspects(cached.prospects);
            setAllTeams(cached.teams);
            calculateStats(cached.prospects);
            setLoading(false);
        }
        try {
            const data = await swrFetch(baseKey, async () => {
                const [prospectsRes, teamsRes] = await Promise.all([
                    prospectsAPI.getAll({ page_size: 100000 }, { timeout: 60000 }),
                    teamsV2API.getAll().catch(() => ({ data: [] })),
                ]);
                return { prospects: prospectsRes.data || [], teams: teamsRes.data || [] };
            });
            const allProspects = data.prospects;
            setProspects(allProspects);
            setAllTeams(data.teams);

            if (isVP || isAdminFull || isAdmin) {
                try {
                    if (isVP) {
                        const teamRes = await usersAPI.getVPTeam(user.id);
                        setTeamMembers(teamRes.data || []);
                    } else {
                        const usersRes = await usersAPI.getAll();
                        setTeamMembers(usersRes.data || []);
                        setAllUsers(usersRes.data || []);
                    }
                } catch (e) {
                    console.error('Error loading team:', e);
                }
            }

            // Pour Admin: charger tous les utilisateurs
            if (isAdminFull || isAdmin) {
                try {
                    const usersRes = await usersAPI.getAll();
                    setAllUsers(usersRes.data || []);
                } catch (e) {
                    console.error('Error loading all users:', e);
                }
            }

            calculateStats(allProspects);
        } catch (error) {
            console.error('Error loading dashboard:', error);
            if (cached) {
                // On a déjà des données affichées : ne pas écraser par une erreur
            } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                setLoadError('Le chargement prend plus de temps que prévu. Veuillez réessayer.');
            } else if (!error.response) {
                setLoadError('Problème de connexion au serveur. Vérifiez votre connexion internet.');
            } else {
                setLoadError('Erreur lors du chargement du tableau de bord. Veuillez réessayer.');
            }
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (allProspects, filters = advancedFilters) => {
        // ============================================================
        // LOGIQUE ALIGNÉE SUR reporting_service.py
        // Les dossiers "gagnés" sont filtrés par easy_power_stage_changed_at
        // (date du changement de stage), PAS par created_at.
        // ============================================================
        
        // Helper: déterminer les bornes de période (uniquement via advancedFilters)
        const periodStart = filters.dateFrom || null;
        const periodEnd = filters.dateTo || null;
        
        // Helper: vérifie si une date ISO tombe dans la période
        const isInPeriod = (dateStr) => {
            if (!periodStart || !periodEnd || !dateStr) return false;
            const d = new Date(dateStr);
            return d >= periodStart && d <= periodEnd;
        };
        
        const hasPeriodFilter = !!(periodStart && periodEnd);
        
        // 1. Filtrage par scope (équipe ou utilisateur) - Admin uniquement
        let scopeFiltered = allProspects;
        if ((isAdminFull || isAdmin) && filters.scopeType !== 'all') {
            if (filters.scopeType === 'team' && filters.teamId) {
                const team = allTeams.find(t => t.id === filters.teamId);
                if (team) {
                    const teamMemberIds = team.members?.map(m => m.user_id) || [];
                    if (team.vp_id) teamMemberIds.push(team.vp_id);
                    
                    scopeFiltered = allProspects.filter(p => 
                        teamMemberIds.includes(p.dossier_apporteur_id) ||
                        teamMemberIds.includes(p.dossier_apporteur_2_id) ||
                        teamMemberIds.includes(p.dossier_vp_id) ||
                        teamMemberIds.includes(p.dossier_vp_2_id) ||
                        teamMemberIds.includes(p.assigned_to)
                    );
                }
            } else if (filters.scopeType === 'user' && filters.userId) {
                scopeFiltered = allProspects.filter(p => 
                    p.dossier_apporteur_id === filters.userId ||
                    p.dossier_apporteur_2_id === filters.userId ||
                    p.dossier_vp_id === filters.userId ||
                    p.dossier_vp_2_id === filters.userId ||
                    p.assigned_to === filters.userId
                );
            }
        }
        
        // 2. Filtrage par visibilité selon le rôle (VP et Apporteur)
        let visibleProspects = scopeFiltered;
        if (isApporteur) {
            visibleProspects = scopeFiltered.filter(p => 
                p.dossier_apporteur_id === user?.id || p.assigned_to === user?.id
            );
        } else if (isVP) {
            const teamIds = teamMembers.map(m => m.id);
            teamIds.push(user?.id);
            visibleProspects = scopeFiltered.filter(p => 
                teamIds.includes(p.dossier_apporteur_id) || 
                p.dossier_vp_id === user?.id ||
                p.dossier_vp_2_id === user?.id ||
                teamIds.includes(p.assigned_to)
            );
        }
        
        // 3. Stages gagnés — RÈGLE MÉTIER UNIQUE alignée sur le backend (models/base.py)
        const VALIDATED_EP_STAGES = ['ep_gagne', 'ep_questionnaire_chantier'];
        const VALIDATED_CH_STAGES = ['ch_devis_accepte', 'ch_devis_signe', 'ch_dossier_subvention', 'ch_subvention_obtenue', 'ch_planification', 'ch_en_cours', 'ch_chantier_en_cours', 'ch_termine', 'ch_chantier_termine', 'ch_attente_paiement', 'ch_paiement_effectue', 'ch_valide', 'ch_gagne'];
        const LOST_EP_STAGES = ['ep_perdu'];
        const LOST_CH_STAGES = ['ch_perdu'];
        
        // 4. Dossiers gagnés : filtrés par easy_power_stage_changed_at dans la période
        const validated = visibleProspects.filter(p => {
            const epStage = p.easy_power_stage;
            const chStage = p.chantier_stage;
            const isWonEP = VALIDATED_EP_STAGES.includes(epStage);
            const isWonCH = VALIDATED_CH_STAGES.includes(chStage);
            
            if (!isWonEP && !isWonCH) return false;
            
            // Si pas de filtre de période, on montre tous les gagnés
            if (!hasPeriodFilter) return true;
            
            // Avec filtre de période : vérifier que le stage a changé DANS la période
            const epChangedInPeriod = isWonEP && isInPeriod(p.easy_power_stage_changed_at);
            const chChangedInPeriod = isWonCH && isInPeriod(p.chantier_stage_changed_at);
            return epChangedInPeriod || chChangedInPeriod;
        });
        
        // 5. Dossiers en cours : filtrés par easy_power_stage_changed_at dans la période
        const inProgress = visibleProspects.filter(p => {
            const epStage = p.easy_power_stage;
            const chStage = p.chantier_stage;
            const isWon = VALIDATED_EP_STAGES.includes(epStage) || VALIDATED_CH_STAGES.includes(chStage);
            const isLost = LOST_EP_STAGES.includes(epStage) || LOST_CH_STAGES.includes(chStage);
            if (isWon || isLost || (!epStage && !chStage)) return false;
            
            if (!hasPeriodFilter) return true;
            
            // Avec filtre de période : vérifier que le stage a changé DANS la période
            return isInPeriod(p.easy_power_stage_changed_at) || isInPeriod(p.chantier_stage_changed_at);
        });
        
        // 5b. Dossiers perdus : filtrés par date de passage
        const lost = visibleProspects.filter(p => {
            const isLostEP = LOST_EP_STAGES.includes(p.easy_power_stage);
            const isLostCH = LOST_CH_STAGES.includes(p.chantier_stage);
            if (!isLostEP && !isLostCH) return false;
            
            if (!hasPeriodFilter) return true;
            
            return (isLostEP && isInPeriod(p.easy_power_stage_changed_at)) || 
                   (isLostCH && isInPeriod(p.chantier_stage_changed_at));
        });
        
        // 6. Fonction pour calculer la commission selon le rôle
        const getCommissionForUser = (prospect) => {
            if (isAdminFull || isAdmin) {
                return (prospect.ep_commission_valnexo || 0) + (prospect.ch_commission_valnexo || 0);
            } else if (isVP) {
                let total = 0;
                if (prospect.dossier_vp_id === user?.id || prospect.dossier_vp_2_id === user?.id) {
                    total += (prospect.ep_commission_vp || 0) + (prospect.ch_commission_vp || 0);
                }
                if (prospect.dossier_apporteur_id || prospect.dossier_apporteur_2_id) {
                    total += (prospect.ep_commission_apporteur || 0) + (prospect.ch_commission_apporteur || 0);
                }
                return total;
            } else if (isApporteur) {
                const isApporteur1 = prospect.dossier_apporteur_id === user?.id;
                const isApporteur2 = prospect.dossier_apporteur_2_id === user?.id;
                const isAssignedTo = prospect.assigned_to === user?.id;
                
                if (isApporteur1 || isAssignedTo) {
                    const epComm1 = prospect.ep_commission_apporteur_1 || 0;
                    const epCommTotal = prospect.ep_commission_apporteur || 0;
                    const chComm1 = prospect.ch_commission_apporteur_1 || 0;
                    const chCommTotal = prospect.ch_commission_apporteur || 0;
                    return (epComm1 > 0 ? epComm1 : epCommTotal) + (chComm1 > 0 ? chComm1 : chCommTotal);
                } else if (isApporteur2) {
                    return (prospect.ep_commission_apporteur_2 || 0) + (prospect.ch_commission_apporteur_2 || 0);
                }
                return (prospect.ep_commission_apporteur || 0) + (prospect.ch_commission_apporteur || 0);
            }
            return 0;
        };
        
        // 7. Commissions VALIDÉES (dossiers gagnés dans la période)
        const commissionsValidees = validated.reduce((sum, p) => sum + getCommissionForUser(p), 0);
        
        // 8. Commissions À ALLER CHERCHER (pas gagnés, pas perdus, avec commission)
        const prospectsAvecCommissionNonGagne = visibleProspects.filter(p => {
            const hasCommission = getCommissionForUser(p) > 0;
            const epStage = p.easy_power_stage;
            const chStage = p.chantier_stage;
            const isWon = VALIDATED_EP_STAGES.includes(epStage) || VALIDATED_CH_STAGES.includes(chStage);
            const isLost = LOST_EP_STAGES.includes(epStage) || LOST_CH_STAGES.includes(chStage);
            return hasCommission && !isWon && !isLost;
        });
        
        const commissionsAAllerChercher = prospectsAvecCommissionNonGagne.reduce((sum, p) => sum + getCommissionForUser(p), 0);
        
        // 9. Total prospects : si période, compter ceux dont le stage a changé dans la période
        let totalForDisplay = visibleProspects.length;
        if (hasPeriodFilter) {
            const activeInPeriod = visibleProspects.filter(p => 
                isInPeriod(p.easy_power_stage_changed_at) || 
                isInPeriod(p.chantier_stage_changed_at) ||
                isInPeriod(p.created_at)
            );
            totalForDisplay = activeInPeriod.length;
        }
        
        setStats({
            totalProspects: totalForDisplay,
            inProgressCount: inProgress.length,
            validatedCount: validated.length,
            lostCount: lost.length,
            totalCommission: commissionsValidees,
            avgCommission: validated.length > 0 ? commissionsValidees / validated.length : 0,
            commissionsValidees,
            commissionsAAllerChercher,
            prospectsGagnes: validated,
            prospectsAAllerChercher: prospectsAvecCommissionNonGagne
        });
    };
    
    const loadTasksData = async () => {
        try {
            const [tasksRes, statsRes] = await Promise.all([
                tasksAPI.getAll(),
                tasksAPI.getStats()
            ]);
            setTasksData({ tasks: tasksRes.data || [], stats: statsRes.data || {} });
        } catch (err) {
            setTasksData({ tasks: [], stats: {} });
        }
    };

    const loadEventsData = async () => {
        try {
            const res = await calendarAPI.getMyEvents();
            setEventsData({ events: res.data || [] });
        } catch (err) {
            setEventsData({ events: [] });
        }
    };

    const loadCommissionData = async () => {
        try {
            const commissionsRes = await ledgerAPI.getCommissions();
            const commissions = commissionsRes.data || [];
            
            const now = new Date();
            const thisMonth = now.getMonth();
            const thisYear = now.getFullYear();
            const thisQuarter = Math.floor(thisMonth / 3);
            
            const totalByPeriod = { today: 0, week: 0, month: 0, quarter: 0, year: 0, all: 0 };
            const totalByPipeline = { easy_power: 0, chantier: 0 };
            const totalByStatus = { draft: 0, validee: 0, facturee: 0, payee: 0 };
            
            commissions.forEach(c => {
                const created = new Date(c.created_at);
                const amount = c.montant_global || 0;
                
                totalByStatus[c.statut] = (totalByStatus[c.statut] || 0) + amount;
                totalByPipeline[c.pipeline_type] = (totalByPipeline[c.pipeline_type] || 0) + amount;
                totalByPeriod.all += amount;
                
                if (created.getFullYear() === thisYear) {
                    totalByPeriod.year += amount;
                    const cQuarter = Math.floor(created.getMonth() / 3);
                    if (cQuarter === thisQuarter) totalByPeriod.quarter += amount;
                    if (created.getMonth() === thisMonth) {
                        totalByPeriod.month += amount;
                        const weekStart = new Date(now);
                        weekStart.setDate(now.getDate() - now.getDay());
                        weekStart.setHours(0, 0, 0, 0);
                        if (created >= weekStart) totalByPeriod.week += amount;
                        const todayStart = new Date(now);
                        todayStart.setHours(0, 0, 0, 0);
                        if (created >= todayStart) totalByPeriod.today += amount;
                    }
                }
            });
            
            setCommissionData({ commissions, totalByPeriod, totalByPipeline, totalByStatus });
            setStats(prev => ({
                ...prev,
                totalCommission: totalByPeriod.all,
                avgCommission: commissions.length > 0 ? totalByPeriod.all / commissions.length : 0
            }));
        } catch (error) {
            console.error('Error loading commission data:', error);
        }
    };

    const openProspectsDialog = (type) => {
        let filtered = prospects;
        let title = '';
        
        if (isApporteur) {
            filtered = filtered.filter(p => p.assigned_to === user?.id);
        } else if (isVP) {
            const teamIds = teamMembers.map(m => m.id).concat(user?.id);
            filtered = filtered.filter(p => teamIds.includes(p.assigned_to));
        }
        
        if (type === 'in_progress') {
            title = 'Dossiers en cours';
            filtered = filtered.filter(p => {
                const epStage = p.easy_power_stage;
                const chStage = p.chantier_stage;
                const isValidated = epStage === 'ep_gagne' || epStage === 'ep_questionnaire_chantier' || chStage === 'ch_valide';
                return !isValidated && (epStage || chStage);
            });
        } else if (type === 'validated') {
            title = 'Dossiers valides';
            filtered = filtered.filter(p => {
                const epStage = p.easy_power_stage;
                const chStage = p.chantier_stage;
                return epStage === 'ep_gagne' || epStage === 'ep_questionnaire_chantier' || chStage === 'ch_valide';
            });
        }
        
        setDialogTitle(title);
        setDialogProspects(filtered);
        setFilterStage('all');
        setShowProspectsDialog(true);
    };

    const getFilteredDialogProspects = () => {
        if (filterStage === 'all') return dialogProspects;
        return dialogProspects.filter(p => p.easy_power_stage === filterStage || p.chantier_stage === filterStage);
    };

    const getProspectStage = (p) => {
        const epStage = p.easy_power_stage;
        const chStage = p.chantier_stage;
        if (epStage && chStage) return `${getStageLabel(epStage)} / ${getStageLabel(chStage)}`;
        return getStageLabel(epStage) || getStageLabel(chStage) || 'N/A';
    };

    const getUniqueStages = () => {
        const stages = new Set();
        dialogProspects.forEach(p => {
            if (p.easy_power_stage) stages.add(p.easy_power_stage);
            if (p.chantier_stage) stages.add(p.chantier_stage);
        });
        return Array.from(stages);
    };

    const getFilteredInProgressProspects = () => {
        let filtered = prospects;
        
        if (isApporteur) {
            filtered = filtered.filter(p => p.assigned_to === user?.id);
        } else if (isVP) {
            const teamIds = teamMembers.map(m => m.id).concat(user?.id);
            filtered = filtered.filter(p => teamIds.includes(p.assigned_to));
        }
        
        filtered = filtered.filter(p => {
            const epStage = p.easy_power_stage;
            const chStage = p.chantier_stage;
            const isValidated = epStage === 'ep_gagne' || epStage === 'ep_questionnaire_chantier' || chStage === 'ch_valide';
            return !isValidated && (epStage || chStage);
        });
        
        if (inProgressPeriod !== 'all') {
            const now = new Date();
            filtered = filtered.filter(p => {
                const created = new Date(p.created_at);
                switch (inProgressPeriod) {
                    case 'today': return created.toDateString() === now.toDateString();
                    case 'week':
                        const weekAgo = new Date(now);
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return created >= weekAgo;
                    case 'month': return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                    case 'quarter':
                        const quarter = Math.floor(now.getMonth() / 3);
                        const pQuarter = Math.floor(created.getMonth() / 3);
                        return pQuarter === quarter && created.getFullYear() === now.getFullYear();
                    case 'year': return created.getFullYear() === now.getFullYear();
                    default: return true;
                }
            });
        }
        
        if (pipelineFilter === 'easy_power') filtered = filtered.filter(p => p.easy_power_stage);
        else if (pipelineFilter === 'chantier') filtered = filtered.filter(p => p.chantier_stage);

        // Filtre par hashtags # (logique "OU"), aligné sur le pipeline sélectionné
        if (selectedHashtags.length > 0) {
            filtered = filtered.filter(p => {
                const ep = p.hashtags_easy_power || [];
                const ch = p.hashtags_chantier || [];
                let pool;
                if (pipelineFilter === 'easy_power') pool = ep;
                else if (pipelineFilter === 'chantier') pool = ch;
                else pool = [...ep, ...ch];
                return selectedHashtags.some(id => pool.includes(id));
            });
        }
        
        if (pipelineFilter !== 'chantier' && selectedEPStages.length > 0) {
            filtered = filtered.filter(p => {
                if (p.easy_power_stage) return selectedEPStages.includes(p.easy_power_stage);
                return pipelineFilter === 'both' && p.chantier_stage;
            });
        }
        
        if (pipelineFilter !== 'easy_power' && selectedChStages.length > 0) {
            filtered = filtered.filter(p => {
                if (p.chantier_stage) return selectedChStages.includes(p.chantier_stage);
                return pipelineFilter === 'both' && p.easy_power_stage;
            });
        }
        
        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(p => {
                const companyName = (p.company_name || '').toLowerCase();
                const firstName = (p.first_name || '').toLowerCase();
                const lastName = (p.last_name || '').toLowerCase();
                const assignedUser = teamMembers.find(m => m.id === p.assigned_to);
                const assignedName = assignedUser ? `${assignedUser.first_name} ${assignedUser.last_name}`.toLowerCase() : '';
                
                return companyName.includes(query) || 
                       firstName.includes(query) || 
                       lastName.includes(query) ||
                       assignedName.includes(query) ||
                       `${firstName} ${lastName}`.includes(query);
            });
        }
        
        // Sorting
        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                let aValue, bValue;
                
                switch (sortConfig.key) {
                    case 'company':
                        aValue = (a.company_name || '').toLowerCase();
                        bValue = (b.company_name || '').toLowerCase();
                        break;
                    case 'contact':
                        aValue = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
                        bValue = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
                        break;
                    case 'assigned':
                        const aUser = teamMembers.find(m => m.id === a.assigned_to);
                        const bUser = teamMembers.find(m => m.id === b.assigned_to);
                        aValue = aUser ? `${aUser.first_name} ${aUser.last_name}`.toLowerCase() : '';
                        bValue = bUser ? `${bUser.first_name} ${bUser.last_name}`.toLowerCase() : '';
                        break;
                    case 'date':
                        aValue = new Date(a.created_at).getTime();
                        bValue = new Date(b.created_at).getTime();
                        break;
                    default:
                        return 0;
                }
                
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        
        return filtered;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const toggleEPStage = (stage) => {
        setSelectedEPStages(prev => prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]);
    };
    
    const toggleChStage = (stage) => {
        setSelectedChStages(prev => prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]);
    };
    
    const handlePipelineChange = (value) => {
        setPipelineFilter(value);
        if (value === 'easy_power') setSelectedChStages([]);
        else if (value === 'chantier') setSelectedEPStages([]);
    };

    const getRankings = () => {
        if (!isAdminFull && !isAdmin) return [];
        const vps = teamMembers.filter(m => m.role === 'vp');
        return vps.map(vp => {
            const vpApporteurs = teamMembers.filter(m => m.vp_id === vp.id);
            const vpTeamIds = [vp.id, ...vpApporteurs.map(a => a.id)];
            const vpProspects = prospects.filter(p => vpTeamIds.includes(p.assigned_to));
            const validated = vpProspects.filter(p => {
                const epStage = p.easy_power_stage;
                const chStage = p.chantier_stage;
                return epStage === 'ep_gagne' || epStage === 'ep_questionnaire_chantier' || chStage === 'ch_valide';
            });
            return {
                id: vp.id,
                name: `${vp.first_name} ${vp.last_name}`,
                role: 'VP',
                teamSize: vpApporteurs.length,
                totalGain: validated.length * 500,
                dossiers: validated.length
            };
        }).sort((a, b) => b.dossiers - a.dossiers);
    };

    const getApporteurRankings = () => {
        if (!isVP) return [];
        return teamMembers.map(member => {
            const memberProspects = prospects.filter(p => p.assigned_to === member.id);
            const validated = memberProspects.filter(p => {
                const epStage = p.easy_power_stage;
                const chStage = p.chantier_stage;
                return epStage === 'ep_gagne' || epStage === 'ep_questionnaire_chantier' || chStage === 'ch_valide';
            });
            return {
                id: member.id,
                name: `${member.first_name} ${member.last_name}`,
                role: member.role,
                parrain_level: member.parrain_level,
                totalGain: validated.length * 200,
                dossiers: validated.length
            };
        }).sort((a, b) => b.dossiers - a.dossiers);
    };
    
    const getFilteredCommissions = () => {
        if (commissionPeriodFilter === 'all') return commissionData.commissions;
        
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        const thisQuarter = Math.floor(thisMonth / 3);
        
        return commissionData.commissions.filter(c => {
            const created = new Date(c.created_at);
            switch (commissionPeriodFilter) {
                case 'today':
                    const todayStart = new Date(now);
                    todayStart.setHours(0, 0, 0, 0);
                    return created >= todayStart;
                case 'week':
                    const weekStart = new Date(now);
                    weekStart.setDate(now.getDate() - now.getDay());
                    weekStart.setHours(0, 0, 0, 0);
                    return created >= weekStart;
                case 'month': return created.getMonth() === thisMonth && created.getFullYear() === thisYear;
                case 'quarter':
                    const cQuarter = Math.floor(created.getMonth() / 3);
                    return cQuarter === thisQuarter && created.getFullYear() === thisYear;
                case 'year': return created.getFullYear() === thisYear;
                default: return true;
            }
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-64" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // ===== Vue Pilotage V2 avec onglets (Admin & VP) =====
    // 🎯 Focus | 📊 Pilotage | 👥 Équipes | 💰 À gagner | 🎗 Fonds Dirigeants
    if (isAdminFull || isAdmin || isVP) {
        const DIRIGEANT_EMAILS = ['jeremy@valnexo.fr', 'morgan@valnexo.fr', 'laurent@valnexo.fr'];
        const isDirigeant = DIRIGEANT_EMAILS.includes(String(user?.email || '').toLowerCase());
        const hasPayerTeam = allTeams.some(t => t.payer_vp_id === user?.id);
        const hasSupervisedTeams = allTeams.some(t => t.supervisor_vp_id === user?.id && t.payer_vp_id !== user?.id);
        const showEquipesTab = isVP && (hasPayerTeam || hasSupervisedTeams);

        // Onglet actif persisté par utilisateur — mais l'URL prime (deep-link Uatu)
        const TAB_STORAGE_KEY = `valnexo_dashboard_tab_${user?.id || 'me'}`;
        const urlTab = searchParams.get('tab');
        const storedTab = (() => {
            try { return localStorage.getItem(TAB_STORAGE_KEY); } catch { return null; }
        })();
        const validTabs = ['focus', 'pilotage', 'equipes', 'agagner', 'fonds'];
        const defaultTab = (urlTab && validTabs.includes(urlTab))
            ? urlTab
            : (storedTab && validTabs.includes(storedTab) ? storedTab : 'focus');

        return (
            <DashboardLayout>
                {/* ===== BANDEAUX HAUT (hors onglets) ===== */}
                <div className="mb-4 space-y-4">
                    <ParrainageIntroBanner userRole={user?.role} />
                    <FeatureAnnouncement />
                    <AcademyUpdateBanner />
                    {/* 👋 Bannière personnalisée par rôle */}
                    <PersonalizedGreetingBanner />
                    {/* 🧿 Uatu Watcher — bulle conversationnelle bienveillante en tête du Dashboard */}
                    <UatuGreetingBubble />
                    {/* ✨ Welcome Uatu — s'affiche au 1er login admin, propose config conversationnelle */}
                    <UatuWelcomeBanner />
                    {/* 🌅 Push matinal — surveille les dossiers critiques une fois par jour */}
                    <UatuDailyPushWatcher />
                    {/* 🧿 Nudge manager — Uatu propose son aide plusieurs fois par session (invisible) */}
                    <UatuNudgeManager />
                </div>

                {/* ===== ONGLETS DE NAVIGATION ===== */}
                <TooltipProvider delayDuration={250}>
                <Tabs
                    defaultValue={defaultTab}
                    onValueChange={(v) => { try { localStorage.setItem(TAB_STORAGE_KEY, v); } catch { /* noop */ } }}
                    className="w-full pb-24 sm:pb-4"
                    data-testid="dashboard-tabs"
                >
                    {/* Barre d'onglets — volontairement TRÈS visible pour signaler que
                        le dashboard est segmenté en plusieurs vues (brief fév. 2026 :
                        certains utilisateurs pensaient que leur dashboard était réduit). */}
                    <div className="mb-5" data-testid="dashboard-tabs-hero">
                        <div className="flex items-center gap-2 mb-2 px-1">
                            <span className="text-[11px] font-bold uppercase tracking-widest text-violet-600">Vues du dashboard</span>
                            <span className="text-[11px] text-slate-400">— clique sur un onglet pour changer de perspective</span>
                        </div>
                        <TabsList className="bg-gradient-to-r from-violet-50 via-white to-violet-50 border-2 border-violet-200 shadow-md rounded-2xl p-1.5 h-auto flex-wrap gap-1.5 justify-start w-full">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <TabsTrigger value="focus" data-testid="dashboard-tab-focus" className="text-sm font-semibold rounded-xl data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-violet-100 gap-2 px-4 py-2.5 transition-all">
                                        <Target className="w-4 h-4" /> Focus
                                    </TabsTrigger>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                    <p className="text-xs">Ce qui compte <strong>aujourd&apos;hui</strong> : dossiers à relancer, messages non lus, tes tâches et événements du jour.</p>
                                </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <TabsTrigger value="pilotage" data-testid="dashboard-tab-pilotage" className="text-sm font-semibold rounded-xl data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-violet-100 gap-2 px-4 py-2.5 transition-all">
                                        <PieChart className="w-4 h-4" /> Pilotage
                                    </TabsTrigger>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                    <p className="text-xs">La <strong>vue chiffrée globale</strong> : « À aller chercher », KPIs équipes, résultat Valnexo, dossiers gagnés/en cours/en retard.</p>
                                </TooltipContent>
                            </Tooltip>
                            {showEquipesTab && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <TabsTrigger value="equipes" data-testid="dashboard-tab-equipes" className="text-sm font-semibold rounded-xl data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-violet-100 gap-2 px-4 py-2.5 transition-all">
                                            <User className="w-4 h-4" /> Équipes
                                        </TabsTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-xs">
                                        <p className="text-xs">Tes équipes <strong>payeur</strong> et <strong>supervisées</strong>, dissociées. Composition, classement, badges retard/inactivité.</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <TabsTrigger value="agagner" data-testid="dashboard-tab-agagner" className="text-sm font-semibold rounded-xl data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-violet-100 gap-2 px-4 py-2.5 transition-all">
                                        <Calculator className="w-4 h-4" /> À gagner
                                    </TabsTrigger>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                    <p className="text-xs"><strong>Primes de secteur</strong> et opportunités bonus pour ton portefeuille.</p>
                                </TooltipContent>
                            </Tooltip>
                            {isDirigeant && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <TabsTrigger value="fonds" data-testid="dashboard-tab-fonds" className="text-sm font-semibold rounded-xl data-[state=active]:bg-violet-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:scale-[1.02] data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-violet-100 gap-2 px-4 py-2.5 transition-all">
                                            <Briefcase className="w-4 h-4" /> Fonds Dirigeants
                                        </TabsTrigger>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-xs">
                                        <p className="text-xs"><strong>Fonds Développement Valnexo</strong> — réservé aux 3 dirigeants (Jérémy, Morgan, Laurent).</p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </TabsList>
                    </div>

                    {/* ===== 🎯 FOCUS — Ce qui compte aujourd'hui ===== */}
                    <TabsContent value="focus" className="space-y-6 mt-0" data-testid="tab-content-focus">
                        {/* Widgets stables en premier (pas de layout shift à l'arrivée) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <UnsecuredProspectsWidget />
                        </div>
                        {/* 3 colonnes : Messages du mur · Tâches · Événements
                            Placé HAUT et STABLE pour que le clic sur un message
                            ne soit jamais déplacé par l'arrivée async de directives. */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="dashboard-3col">
                            <WallUnreadWidget />
                            <DashboardTasksWidget tasks={tasksData.tasks} stats={tasksData.stats} />
                            <DashboardEventsWidget events={eventsData.events} />
                        </div>
                        {/* Directives Direction — apparaissent async (returns null en loading).
                            Placées SOUS la ligne messages pour qu'elles ne poussent jamais
                            les widgets stables (empêche le bounce lors du clic sur un message). */}
                        <DirectivesWidget />
                        {/* Admin : suivi live des directives que J'AI lancées */}
                        <MyDirectivesStatusWidget isAdminFull={isAdminFull} />
                        {/* Admin : commissions à renseigner (déplacé depuis Pilotage — brief fév. 2026) */}
                        {isAdminFull && <CommissionsPendingWidget />}
                        <AuditedDossiersWidget />
                    </TabsContent>

                    {/* ===== 📊 PILOTAGE — Vue chiffrée globale ===== */}
                    <TabsContent value="pilotage" className="space-y-6 mt-0" data-testid="tab-content-pilotage">
                        <PilotageDashboard user={user} />
                    </TabsContent>

                    {/* ===== 👥 ÉQUIPES — Vues VP dissociées ===== */}
                    {showEquipesTab && (
                        <TabsContent value="equipes" className="space-y-6 mt-0" data-testid="tab-content-equipes">
                            {hasPayerTeam && (
                                <VPPayeurDashboardView
                                    user={user}
                                    prospects={prospects}
                                    teamMembers={teamMembers}
                                    teams={allTeams}
                                    advancedFilters={advancedFilters}
                                />
                            )}
                            {hasSupervisedTeams && (
                                <VPSuperviseurDashboardView
                                    user={user}
                                    prospects={prospects}
                                    teamMembers={teamMembers}
                                    teams={allTeams}
                                    allUsers={allUsers}
                                    advancedFilters={advancedFilters}
                                />
                            )}
                        </TabsContent>
                    )}

                    {/* ===== 💰 À GAGNER — Primes de secteur ===== */}
                    <TabsContent value="agagner" className="space-y-6 mt-0" data-testid="tab-content-agagner">
                        {isVP && <PrimeSecteurWidget />}
                        {(isAdmin || isAdminFull) && <PrimeSecteurAdminOverview />}
                    </TabsContent>

                    {/* ===== 🎗 FONDS DIRIGEANTS — Dirigeants Valnexo uniquement ===== */}
                    {isDirigeant && (
                        <TabsContent value="fonds" className="space-y-6 mt-0" data-testid="tab-content-fonds">
                            <DevFundCard user={user} />
                        </TabsContent>
                    )}
                </Tabs>
                </TooltipProvider>
            </DashboardLayout>
        );
    }

    // Afficher erreur de chargement au lieu de page blanche
    if (loadError) {
        return (
            <DashboardLayout>
                <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                            <X className="w-8 h-8 text-orange-600" />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">
                            Problème de chargement
                        </h2>
                        <p className="text-slate-600 mb-4 max-w-md">
                            {loadError}
                        </p>
                        <Button 
                            onClick={() => {
                                setLoading(true);
                                setLoadError(null);
                                loadDashboard();
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            Réessayer
                        </Button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    const rankings = getRankings();
    const apporteurRankings = getApporteurRankings();

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* 🎗️ Zone TOP — Bannières temporaires */}
                <FeatureAnnouncement />
                <AcademyUpdateBanner />

                {/* 👋 Bannière personnalisée par rôle (nouvelle refonte Journal) */}
                <PersonalizedGreetingBanner />

                {/* 🧿 Uatu Watcher — bulle conversationnelle bienveillante */}
                <UatuGreetingBubble />
                {/* 🧿 Nudge manager — Uatu propose son aide plusieurs fois par session */}
                <UatuNudgeManager />

                {/* === CONTENU DASHBOARD EXISTANT PRÉSERVÉ === */}

                {/* Header historique (masqué visuellement — remplacé par PersonalizedGreetingBanner) */}
                <div className="hidden">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                            Bonjour, {user?.first_name} !
                        </h1>
                        <p className="text-slate-600 mt-1 text-sm sm:text-base">
                            Voici votre tableau de bord
                            {isAdminFull && ' - Vue Admin Full'}
                            {isAdmin && ' - Vue Admin'}
                            {isVP && ' - Vue VP'}
                            {isApporteur && ' - Vue Apporteur'}
                        </p>
                    </div>
                </div>

                {/* Header original (préservé pour compatibilité, sera nettoyé prochaine session) */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="hidden">
                            <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">
                                Bonjour, {user?.first_name} !
                            </h1>
                            <p className="text-slate-600 mt-1 text-sm sm:text-base">
                                Voici votre tableau de bord
                                {isAdminFull && ' - Vue Admin Full'}
                                {isAdmin && ' - Vue Admin'}
                                {isVP && ' - Vue VP'}
                                {isApporteur && ' - Vue Apporteur'}
                            </p>
                        </div>
                    </div>
                    
                    {/* Filtres Avancés */}
                    <DashboardFilters
                        teams={allTeams}
                        users={allUsers}
                        teamMembers={teamMembers}
                        onFiltersChange={handleFiltersChange}
                        currentUserRole={user?.role}
                        currentUserId={user?.id}
                        initialFilters={advancedFilters}
                    />
                </div>

                {/* Quick Access Cards : En cours / Gagnés / En retard - TOUT EN HAUT */}
                <QuickAccessCards period={period} />

                {/* Encart "Nouveaux messages sur vos dossiers" */}
                <WallUnreadWidget />

                {/* Encart "Dossiers audités" (rôle Auditeur) */}
                <AuditedDossiersWidget />

                {/* Fil d'Actualités - EN HAUT pour visibilité maximale */}
                <NewsFeedWidget />

                {/* ===== VUES PERSONNALISÉES PAR RÔLE ===== */}
                
                {/* Vue Apporteur - Mes performances */}
                {isApporteur && (
                    <ApporteurDashboardView 
                        user={user}
                        prospects={prospects}
                        commissions={commissionData.commissions}
                        advancedFilters={advancedFilters}
                    />
                )}
                
                {/* Vue VP Payeur - Mon équipe (affichée pour TOUT VP qui est payeur d'au moins une équipe,
                    y compris s'il est aussi superviseur d'autres équipes → vues bien DISSOCIÉES) */}
                {isVP && allTeams.some(t => t.payer_vp_id === user?.id) && (
                    <VPPayeurDashboardView 
                        user={user}
                        prospects={prospects}
                        teamMembers={teamMembers}
                        teams={allTeams}
                        advancedFilters={advancedFilters}
                    />
                )}
                
                {/* Vue VP Superviseur - Équipes supervisées UNIQUEMENT (dissociées de l'équipe personnelle) */}
                {isVP && allTeams.some(t => t.supervisor_vp_id === user?.id) && (
                    <VPSuperviseurDashboardView 
                        user={user}
                        prospects={prospects}
                        teamMembers={teamMembers}
                        teams={allTeams}
                        allUsers={allUsers}
                        advancedFilters={advancedFilters}
                    />
                )}
                
                {/* Vue Admin - Vue globale */}
                {(isAdminFull || isAdmin) && (
                    <AdminDashboardView 
                        prospects={prospects}
                        allUsers={allUsers}
                        teams={allTeams}
                        commissions={commissionData.commissions}
                        dashboardStats={stats}
                        advancedFilters={advancedFilters}
                    />
                )}

                {/* Widget Commissions à renseigner - Admin Full uniquement */}
                {isAdminFull && (
                    <CommissionsPendingWidget />
                )}

                {/* Widget Dossiers en retard - Admin/VP uniquement */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <OverdueProspectsWidget />
                    <UnsecuredProspectsWidget />
                </div>

                {/* ===== SECTIONS COMMUNES ===== */}

                {/* Stats Cards */}
                <DashboardStatsCards 
                    stats={stats}
                    onValidatedClick={() => openProspectsDialog('validated')}
                    onCommissionClick={() => setShowCommissionDialog(true)}
                    onProspectsClick={() => navigate('/prospects')}
                    onInProgressClick={() => {
                        const section = document.querySelector('[data-testid="in-progress-section"]');
                        if (section) {
                            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }}
                    onCommissionsValideesClick={() => {
                        setCommissionDetailsType('validees');
                        setShowCommissionDetailsDialog(true);
                    }}
                    onCommissionsACherchererClick={() => {
                        setCommissionDetailsType('a_chercher');
                        setShowCommissionDetailsDialog(true);
                    }}
                />

                {/* In Progress Section */}
                <InProgressSection 
                    prospects={getFilteredInProgressProspects()}
                    pipelineFilter={pipelineFilter}
                    inProgressPeriod={inProgressPeriod}
                    selectedEPStages={selectedEPStages}
                    selectedChStages={selectedChStages}
                    onPipelineChange={handlePipelineChange}
                    onPeriodChange={setInProgressPeriod}
                    onToggleEPStage={toggleEPStage}
                    onToggleChStage={toggleChStage}
                    onClearEPStages={() => setSelectedEPStages([])}
                    onClearChStages={() => setSelectedChStages([])}
                    navigate={navigate}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    selectedHashtags={selectedHashtags}
                    onHashtagsChange={setSelectedHashtags}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    teamMembers={teamMembers}
                    currentUser={user}
                    onUserClick={(userId) => {
                        setSelectedUserId(userId);
                        setShowUserProfileModal(true);
                    }}
                />

                {/* Second Row - Widgets */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <DashboardEventsWidget events={eventsData.events} />
                    <DashboardTasksWidget tasks={tasksData.tasks} stats={tasksData.stats} />
                    {isAdminFull && <BankAccountPlaceholder />}
                </div>

                {/* Rankings */}
                {(isAdminFull || isAdmin) && rankings.length > 0 && (
                    <VPRankings 
                        rankings={rankings} 
                        onUserClick={(userId) => {
                            setSelectedUserId(userId);
                            setShowUserProfileModal(true);
                        }}
                    />
                )}
                {isVP && apporteurRankings.length > 0 && (
                    <ApporteurRankings 
                        rankings={apporteurRankings}
                        onUserClick={(userId) => {
                            setSelectedUserId(userId);
                            setShowUserProfileModal(true);
                        }}
                    />
                )}

                {/* Quick Actions */}
                <DashboardQuickActions />
            </div>

            {/* Prospects Dialog */}
            <ProspectsDialog 
                open={showProspectsDialog}
                onOpenChange={setShowProspectsDialog}
                title={dialogTitle}
                prospects={getFilteredDialogProspects()}
                filterStage={filterStage}
                onFilterChange={setFilterStage}
                stages={getUniqueStages()}
                getProspectStage={getProspectStage}
                getStageLabel={getStageLabel}
                navigate={navigate}
            />
            
            {/* Commission Dialog */}
            <CommissionDialog 
                open={showCommissionDialog}
                onOpenChange={setShowCommissionDialog}
                commissionData={commissionData}
                periodFilter={commissionPeriodFilter}
                onPeriodFilterChange={setCommissionPeriodFilter}
                filteredCommissions={getFilteredCommissions()}
                navigate={navigate}
            />
            
            {/* Commission Details Dialog (validées / à aller chercher) */}
            <CommissionDetailsDialog
                open={showCommissionDetailsDialog}
                onOpenChange={setShowCommissionDetailsDialog}
                type={commissionDetailsType}
                prospectsGagnes={stats.prospectsGagnes || []}
                prospectsAChercher={stats.prospectsAAllerChercher || []}
                commissionsValidees={stats.commissionsValidees || 0}
                commissionsAChercher={stats.commissionsAAllerChercher || 0}
                navigate={navigate}
            />
            
            {/* User Profile Modal */}
            <UserProfileModal 
                userId={selectedUserId}
                isOpen={showUserProfileModal}
                onClose={() => setShowUserProfileModal(false)}
            />
        </DashboardLayout>
    );
}

// Sub-components

function InProgressSection({ 
    prospects, pipelineFilter, inProgressPeriod, selectedEPStages, selectedChStages,
    onPipelineChange, onPeriodChange, onToggleEPStage, onToggleChStage,
    onClearEPStages, onClearChStages, navigate, searchQuery, onSearchChange,
    selectedHashtags, onHashtagsChange,
    sortConfig, onSort, teamMembers, currentUser, onUserClick
}) {
    // Helper to get assigned user name
    const getAssignedUserName = (assignedTo) => {
        if (!assignedTo) return '-';
        if (currentUser?.id === assignedTo) {
            return `${currentUser.first_name} ${currentUser.last_name}`;
        }
        const user = teamMembers.find(m => m.id === assignedTo);
        return user ? `${user.first_name} ${user.last_name}` : '-';
    };
    
    // Helper to get role badge
    const getUserRoleBadge = (assignedTo) => {
        if (!assignedTo) return null;
        let role = null;
        if (currentUser?.id === assignedTo) {
            role = currentUser.role;
        } else {
            const user = teamMembers.find(m => m.id === assignedTo);
            role = user?.role;
        }
        
        if (!role) return null;
        
        const roleLabels = {
            'admin_full': { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
            'admin': { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
            'vp': { label: 'VP', color: 'bg-blue-100 text-blue-700' },
            'apporteur': { label: 'Apporteur', color: 'bg-green-100 text-green-700' }
        };
        
        const config = roleLabels[role];
        return config ? <Badge className={`text-xs ${config.color}`}>{config.label}</Badge> : null;
    };
    
    // Sort icon component
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) {
            return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-slate-400" />;
        }
        return sortConfig.direction === 'asc' 
            ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-blue-600" />
            : <ArrowDown className="w-3.5 h-3.5 ml-1 text-blue-600" />;
    };
    
    return (
        <Card data-testid="in-progress-section">
            <CardHeader className="pb-4">
                <div className="flex flex-col gap-4">
                    {/* Title row */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <CardTitle className="flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                            Dossiers en cours
                            <Badge variant="secondary" className="ml-2">{prospects.length} dossiers</Badge>
                        </CardTitle>
                        
                        {/* Search bar */}
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                type="text"
                                placeholder="Rechercher entreprise, contact, responsable..."
                                value={searchQuery}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className="pl-9 h-9"
                                data-testid="search-dossiers"
                            />
                            {searchQuery && (
                                <button 
                                    onClick={() => onSearchChange('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                                >
                                    <X className="w-3.5 h-3.5 text-slate-400" />
                                </button>
                            )}
                        </div>
                    </div>
                    
                    {/* Filters row */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                            <Label className="text-sm text-slate-500 whitespace-nowrap">Période :</Label>
                            <Select value={inProgressPeriod} onValueChange={onPeriodChange}>
                                <SelectTrigger className="w-40" data-testid="filter-period">
                                    <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {IN_PROGRESS_PERIOD_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Label className="text-sm text-slate-500 whitespace-nowrap">Pipeline :</Label>
                            <Select value={pipelineFilter} onValueChange={onPipelineChange}>
                                <SelectTrigger className="w-36" data-testid="filter-pipeline">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PIPELINE_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {(pipelineFilter === 'both' || pipelineFilter === 'easy_power') && (
                            <StageFilterPopover 
                                stages={EASY_POWER_STAGES}
                                selected={selectedEPStages}
                                onToggle={onToggleEPStage}
                                onClear={onClearEPStages}
                                label="Étapes Courtage"
                                icon={<Zap className="w-4 h-4 text-yellow-500" />}
                                testId="filter-ep-stages"
                            />
                        )}
                        
                        {(pipelineFilter === 'both' || pipelineFilter === 'chantier') && (
                            <StageFilterPopover 
                                stages={CHANTIER_STAGES}
                                selected={selectedChStages}
                                onToggle={onToggleChStage}
                                onClear={onClearChStages}
                                label="Étapes Travaux d’optimisation"
                                icon={<Target className="w-4 h-4 text-orange-500" />}
                                testId="filter-ch-stages"
                            />
                        )}

                        <div className="flex items-center gap-2">
                            <Label className="text-sm text-slate-500 whitespace-nowrap">Hashtags :</Label>
                            <HashtagFilter selected={selectedHashtags} onChange={onHashtagsChange} className="w-44" />
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {prospects.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="font-medium">Aucun dossier trouvé</p>
                        <p className="text-xs mt-1">Modifiez vos filtres pour voir plus de resultats</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead 
                                        className="cursor-pointer hover:bg-slate-50 select-none"
                                        onClick={() => onSort('company')}
                                        data-testid="sort-company"
                                    >
                                        <div className="flex items-center">
                                            Entreprise
                                            <SortIcon columnKey="company" />
                                        </div>
                                    </TableHead>
                                    <TableHead 
                                        className="cursor-pointer hover:bg-slate-50 select-none"
                                        onClick={() => onSort('contact')}
                                        data-testid="sort-contact"
                                    >
                                        <div className="flex items-center">
                                            Contact
                                            <SortIcon columnKey="contact" />
                                        </div>
                                    </TableHead>
                                    <TableHead>Pipeline</TableHead>
                                    <TableHead>Etape</TableHead>
                                    <TableHead 
                                        className="cursor-pointer hover:bg-slate-50 select-none"
                                        onClick={() => onSort('assigned')}
                                        data-testid="sort-assigned"
                                    >
                                        <div className="flex items-center">
                                            <User className="w-4 h-4 mr-1" />
                                            Responsable
                                            <SortIcon columnKey="assigned" />
                                        </div>
                                    </TableHead>
                                    <TableHead 
                                        className="cursor-pointer hover:bg-slate-50 select-none"
                                        onClick={() => onSort('date')}
                                        data-testid="sort-date"
                                    >
                                        <div className="flex items-center">
                                            Date
                                            <SortIcon columnKey="date" />
                                        </div>
                                    </TableHead>
                                    {currentUser?.role === 'apporteur' && (
                                        <TableHead>Ma commission</TableHead>
                                    )}
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {prospects.slice(0, 10).map(p => (
                                    <TableRow 
                                        key={p.id} 
                                        data-testid={`prospect-row-${p.id}`}
                                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                                        onClick={() => navigate(`/prospects/${p.id}`)}
                                    >
                                        <TableCell className="font-medium">{p.company_name}</TableCell>
                                        <TableCell>{p.first_name} {p.last_name}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                {p.easy_power_stage && (
                                                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                                        <Zap className="w-3 h-3 mr-1" />Courtage
                                                    </Badge>
                                                )}
                                                {p.chantier_stage && (
                                                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                                        <Target className="w-3 h-3 mr-1" />Travaux
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1 text-xs">
                                                {p.easy_power_stage && <span className="text-yellow-700">{getStageLabel(p.easy_power_stage)}</span>}
                                                {p.chantier_stage && <span className="text-orange-700">{getStageLabel(p.chantier_stage)}</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div 
                                                className="flex items-center gap-2 cursor-pointer hover:text-yellow-600 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (p.assigned_to && onUserClick) {
                                                        onUserClick(p.assigned_to);
                                                    }
                                                }}
                                                data-testid={`user-click-${p.assigned_to}`}
                                            >
                                                <span className="text-sm underline decoration-dotted underline-offset-2">{getAssignedUserName(p.assigned_to)}</span>
                                                {getUserRoleBadge(p.assigned_to)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-500">
                                            {p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : '-'}
                                        </TableCell>
                                        {currentUser?.role === 'apporteur' && (
                                            <TableCell>
                                                <CommissionCells entries={makeModuleCommissions(p, currentUser?.id)} />
                                            </TableCell>
                                        )}
                                        <TableCell className="text-right">
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/prospects/${p.id}`);
                                                }} 
                                                data-testid={`view-prospect-${p.id}`}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />Voir
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {prospects.length > 10 && (
                            <div className="text-center py-3 border-t">
                                <Button variant="link" onClick={() => navigate('/prospects')}>
                                    Voir tous les {prospects.length} dossiers
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function StageFilterPopover({ stages, selected, onToggle, onClear, label, icon, testId }) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid={testId}>
                    {icon}
                    {label}
                    {selected.length > 0 && <Badge variant="secondary" className="ml-1">{selected.length}</Badge>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="end">
                <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{label}</span>
                        {selected.length > 0 && (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClear}>
                                <X className="w-3 h-3 mr-1" />Effacer
                            </Button>
                        )}
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                        {stages.map(stage => (
                            <div key={stage} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer" onClick={() => onToggle(stage)}>
                                <Checkbox checked={selected.includes(stage)} onCheckedChange={() => onToggle(stage)} />
                                <span className="text-sm">{getStageLabel(stage)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function ProspectsDialog({ open, onOpenChange, title, prospects, filterStage, onFilterChange, stages, getProspectStage, getStageLabel, navigate }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span>{title}</span>
                        <Select value={filterStage} onValueChange={onFilterChange}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Filtrer par étape" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les étapes</SelectItem>
                                {stages.map(stage => (
                                    <SelectItem key={stage} value={stage}>{getStageLabel(stage) || stage}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                    {prospects.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>Aucun dossier trouvé</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Entreprise</TableHead>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Etape</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {prospects.map(p => (
                                    <TableRow key={p.id}>
                                        <TableCell className="font-medium">{p.company_name}</TableCell>
                                        <TableCell>{p.first_name} {p.last_name}</TableCell>
                                        <TableCell><Badge variant="outline">{getProspectStage(p)}</Badge></TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => { onOpenChange(false); navigate(`/prospects/${p.id}`); }}>
                                                Voir
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CommissionDialog({ open, onOpenChange, commissionData, periodFilter, onPeriodFilterChange, filteredCommissions, navigate }) {
    const periodLabels = [
        { key: 'today', label: "Aujourd'hui" },
        { key: 'week', label: 'Cette semaine' },
        { key: 'month', label: 'Ce mois' },
        { key: 'quarter', label: 'Ce trimestre' },
        { key: 'year', label: 'Cette annee' },
        { key: 'all', label: 'Total' }
    ];
    
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-purple-600" />
                        Detail des Commissions
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                    {/* Summary by Period */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {periodLabels.map(({ key, label }) => (
                            <Card 
                                key={key}
                                className={`cursor-pointer transition-all ${periodFilter === key ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:bg-slate-50'}`}
                                onClick={() => onPeriodFilterChange(key)}
                            >
                                <CardContent className="p-3 text-center">
                                    <p className="text-xs text-slate-500">{label}</p>
                                    <p className={`text-lg font-bold ${periodFilter === key ? 'text-purple-700' : 'text-slate-900'}`}>
                                        {(commissionData.totalByPeriod[key] || 0).toLocaleString('fr-FR')} €
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    
                    {/* Summary by Pipeline */}
                    <div className="grid grid-cols-2 gap-4">
                        <Card className="bg-amber-50 border-amber-200">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-amber-700 flex items-center gap-1">
                                            <Zap className="w-4 h-4" /> Courtage
                                        </p>
                                        <p className="text-2xl font-bold text-amber-800">
                                            {(commissionData.totalByPipeline?.easy_power || 0).toLocaleString('fr-FR')} €
                                        </p>
                                    </div>
                                    <PieChart className="w-8 h-8 text-amber-400" />
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-blue-50 border-blue-200">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-blue-700 flex items-center gap-1">
                                            <Briefcase className="w-4 h-4" /> Travaux d’optimisation
                                        </p>
                                        <p className="text-2xl font-bold text-blue-800">
                                            {(commissionData.totalByPipeline?.chantier || 0).toLocaleString('fr-FR')} €
                                        </p>
                                    </div>
                                    <PieChart className="w-8 h-8 text-blue-400" />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* Summary by Status */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Card className="bg-slate-50">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-slate-500">Brouillons</p>
                                <p className="text-lg font-bold text-slate-600">{(commissionData.totalByStatus?.draft || 0).toLocaleString('fr-FR')} €</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-blue-50">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-blue-600">Validees</p>
                                <p className="text-lg font-bold text-blue-700">{(commissionData.totalByStatus?.validee || 0).toLocaleString('fr-FR')} €</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-amber-50">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-amber-600">Facturees</p>
                                <p className="text-lg font-bold text-amber-700">{(commissionData.totalByStatus?.facturee || 0).toLocaleString('fr-FR')} €</p>
                            </CardContent>
                        </Card>
                        <Card className="bg-green-50">
                            <CardContent className="p-3 text-center">
                                <p className="text-xs text-green-600">Payees</p>
                                <p className="text-lg font-bold text-green-700">{(commissionData.totalByStatus?.payee || 0).toLocaleString('fr-FR')} €</p>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* Commission List */}
                    <div>
                        <h3 className="text-sm font-medium text-slate-700 mb-3">Commissions ({filteredCommissions.length})</h3>
                        {filteredCommissions.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                <Calculator className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                <p>Aucune commission pour cette periode</p>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Dossier</TableHead>
                                        <TableHead>Pipeline</TableHead>
                                        <TableHead>VP</TableHead>
                                        <TableHead>Montant</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredCommissions.map(c => (
                                        <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => { onOpenChange(false); navigate('/ledger'); }}>
                                            <TableCell className="font-medium">{c.dossier_name}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={c.pipeline_type === 'easy_power' ? 'text-amber-600' : 'text-blue-600'}>
                                                    {c.pipeline_type === 'easy_power' ? <><Zap className="w-3 h-3 mr-1" />Courtage</> : <><Briefcase className="w-3 h-3 mr-1" />Travaux</>}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{c.vp_name}</TableCell>
                                            <TableCell className="font-bold">{c.montant_global?.toLocaleString('fr-FR')} €</TableCell>
                                            <TableCell>
                                                <Badge className={
                                                    c.statut === 'draft' ? 'bg-slate-100 text-slate-700' :
                                                    c.statut === 'validee' ? 'bg-blue-100 text-blue-700' :
                                                    c.statut === 'facturee' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-green-100 text-green-700'
                                                }>
                                                    {c.statut === 'draft' ? 'Brouillon' : c.statut === 'validee' ? 'Validee' : c.statut === 'facturee' ? 'Facturee' : 'Payee'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-500">{new Date(c.created_at).toLocaleDateString('fr-FR')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                    
                    {/* Link to full ledger */}
                    <div className="text-center pt-4 border-t">
                        <Button variant="outline" onClick={() => { onOpenChange(false); navigate('/ledger'); }}>
                            <Calculator className="w-4 h-4 mr-2" />
                            Voir le Commission Ledger complet
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}


// Dialog pour afficher les prospects avec commissions validées ou à chercher
function CommissionDetailsDialog({ open, onOpenChange, type, prospectsGagnes, prospectsAChercher, commissionsValidees, commissionsAChercher, navigate }) {
    const isValidees = type === 'validees';
    const prospects = isValidees ? prospectsGagnes : prospectsAChercher;
    const totalAmount = isValidees ? commissionsValidees : commissionsAChercher;
    
    const getCommissionAmount = (p) => {
        return (p.ep_commission_valnexo || 0) + (p.ch_commission_valnexo || 0);
    };
    
    const getStageLabel = (p) => {
        const epStage = p.easy_power_stage;
        const chStage = p.chantier_stage;
        
        const labels = {
            'ep_debut_prospection': 'Début prospection',
            'ep_facture_recue': 'Facture reçue',
            'ep_mandat_envoye': 'Mandat envoyé',
            'ep_mandat_signe': 'Mandat signé',
            'ep_dossier_transmis': 'Dossier transmis',
            'ep_etude_recue': 'Étude reçue',
            'ep_etude_expliquee': 'Étude expliquée',
            'ep_rib_receptionne': 'RIB réceptionné',
            'ep_contrat_envoye': 'Contrat envoyé',
            'ep_contrat_explique': 'Attente signature',
            'ep_gagne': 'Gagné',
            'ep_questionnaire_chantier': 'Formulaire rempli',
            'ch_formulaire_rempli': 'Formulaire rempli',
            'ch_visite_planifiee': 'Visite planifiée',
            'ch_visite_effectuee': 'Visite effectuée',
            'ch_dossier_etude': 'À l\'étude',
            'ch_etude_presentee': 'Étude présentée',
            'ch_devis_envoye': 'Devis envoyé',
            'ch_devis_accepte': 'Devis accepté',
            'ch_planification': 'Planification',
            'ch_en_cours': 'En cours',
            'ch_termine': 'Terminé',
            'ch_attente_paiement': 'Attente paiement',
            'ch_paiement_effectue': 'Payé',
            'ch_valide': 'Validé'
        };
        
        if (epStage && chStage) {
            return `${labels[epStage] || epStage} / ${labels[chStage] || chStage}`;
        }
        return labels[epStage] || labels[chStage] || '-';
    };
    
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {isValidees ? (
                            <>
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                                <span>Commissions validées</span>
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 ml-2">
                                    {totalAmount.toLocaleString('fr-FR')} €
                                </Badge>
                            </>
                        ) : (
                            <>
                                <Target className="w-5 h-5 text-orange-600" />
                                <span>Commissions à aller chercher</span>
                                <Badge variant="outline" className="bg-orange-50 text-orange-700 ml-2">
                                    {totalAmount.toLocaleString('fr-FR')} €
                                </Badge>
                            </>
                        )}
                    </DialogTitle>
                </DialogHeader>
                
                <div className="mt-4">
                    {/* Stats résumé */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <Card className={isValidees ? "bg-emerald-50 border-emerald-200" : "bg-orange-50 border-orange-200"}>
                            <CardContent className="p-4">
                                <p className="text-sm text-slate-600">Nombre de dossiers</p>
                                <p className={`text-2xl font-bold ${isValidees ? 'text-emerald-700' : 'text-orange-700'}`}>
                                    {prospects.length}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className={isValidees ? "bg-emerald-50 border-emerald-200" : "bg-orange-50 border-orange-200"}>
                            <CardContent className="p-4">
                                <p className="text-sm text-slate-600">Moyenne par dossier</p>
                                <p className={`text-2xl font-bold ${isValidees ? 'text-emerald-700' : 'text-orange-700'}`}>
                                    {prospects.length > 0 ? Math.round(totalAmount / prospects.length).toLocaleString('fr-FR') : 0} €
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* Liste des prospects */}
                    {prospects.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>Aucun dossier {isValidees ? 'avec commission validée' : 'avec commission à chercher'}</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Entreprise</TableHead>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Étape</TableHead>
                                    <TableHead className="text-right">Commission</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {prospects.map(p => (
                                    <TableRow 
                                        key={p.id} 
                                        className="cursor-pointer hover:bg-slate-50"
                                        onClick={() => { onOpenChange(false); navigate(`/prospects/${p.id}`); }}
                                    >
                                        <TableCell className="font-medium">{p.company_name || '-'}</TableCell>
                                        <TableCell>{p.first_name} {p.last_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={isValidees ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}>
                                                {getStageLabel(p)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-bold">
                                            <span className={isValidees ? "text-emerald-600" : "text-orange-600"}>
                                                {getCommissionAmount(p).toLocaleString('fr-FR')} €
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onOpenChange(false);
                                                    navigate(`/prospects/${p.id}`);
                                                }}
                                            >
                                                <Eye className="w-4 h-4 mr-1" />
                                                Voir
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    
                    {/* Lien vers la liste filtrée */}
                    <div className="text-center pt-4 border-t mt-4">
                        <Button 
                            variant="outline" 
                            onClick={() => { 
                                onOpenChange(false); 
                                // Navigation vers la liste des prospects avec filtre approprié
                                navigate('/prospects');
                            }}
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            Voir tous les prospects
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
